import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from './ui/dropdown-menu';
import {
  Headphones, Music, GraduationCap, Library, Sparkles, Bookmark, FlaskConical, Compass, Radio, ChevronDown, Zap, Upload, Crown,
} from 'lucide-react';

/** Desktop-only top navigation strip. Sits inside <Navbar /> between the logo and the search bar.
 * Shows the 5 most-important destinations as pills, plus a "More" dropdown for the rest.
 * Free-tier items get a tiny "Free" chip so anonymous or low-tier users know what they can try without paying.
 */

const PRIMARY = [
  { to: '/feed',         label: 'Reel',    icon: Headphones, color: '#22d3ee', free: true,  testid: 'topnav-reel' },
  { to: '/artists',      label: 'Artists', icon: Music,      color: '#a78bfa', free: true,  testid: 'topnav-artists' },
  { to: '/academy',      label: 'Academy', icon: GraduationCap, color: '#ffcf52', free: false, testid: 'topnav-academy' },
  { to: '/hub',          label: 'Hub',     icon: Library,    color: '#22d3ee', free: true,  testid: 'topnav-hub' },
];

const MORE = [
  { to: '/income',      label: 'Income tool',     icon: Sparkles,    note: '15+ income streams + advisor' },
  { to: '/royalty',     label: 'Royalty Dashboard', icon: Sparkles,  note: 'Studio · CSV upload + alerts' },
  { to: '/reapercast',  label: 'ReaperCast Radio', icon: Radio,      note: '24/7 indie radio · free' },
  { to: '/migrate',     label: 'Migration Center', icon: Upload,     note: 'Bring catalog from YouTube/Spotify/etc' },
  { to: '/research',    label: 'Research Vault',  icon: Bookmark,    note: 'Save AI outputs' },
  { to: '/changelog',   label: "What's new",      icon: Sparkles,    note: 'Feature updates' },
  { to: '/beta',        label: 'Beta Program',    icon: FlaskConical, note: 'Test new features' },
  { to: '/category/trending', label: 'Explore videos', icon: Compass, note: 'VidCreForge' },
  { to: '/shorts',      label: 'Shorts',          icon: Zap,         note: 'Quick clips' },
];

export default function TopNav() {
  return (
    <nav className="hidden lg:flex items-center gap-1 shrink-0" data-testid="topnav">
      {PRIMARY.map(item => (
        <NavLink key={item.to} to={item.to} data-testid={item.testid}
          className={({ isActive }) => `
            group relative px-3 py-1.5 rounded-full text-[13px] font-semibold transition-all
            flex items-center gap-1.5 hover:bg-white/[0.06]
            ${isActive ? 'bg-white/[0.08] text-white' : 'text-white/75 hover:text-white'}
          `}>
          <item.icon className="w-4 h-4" style={{ color: item.color }} />
          <span>{item.label}</span>
          {item.free && (
            <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider uppercase"
                  style={{ background: 'rgba(52,211,153,0.16)', color: '#34d399', border: '1px solid rgba(52,211,153,0.4)' }}>
              Free
            </span>
          )}
        </NavLink>
      ))}

      <DropdownMenu>
        <DropdownMenuTrigger className="outline-none group">
          <div className="px-3 py-1.5 rounded-full text-[13px] font-semibold text-white/75 hover:text-white hover:bg-white/[0.06] flex items-center gap-1 transition-all" data-testid="topnav-more">
            More <ChevronDown className="w-3.5 h-3.5 opacity-60 group-data-[state=open]:rotate-180 transition-transform" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="bg-[var(--theme-surface)] border-white/10 text-[var(--theme-fg)] w-64 p-1.5">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-white/45 font-bold">Discover</DropdownMenuLabel>
          {MORE.map(item => (
            <DropdownMenuItem key={item.to} asChild>
              <Link to={item.to} className="flex items-start gap-3 py-2.5 cursor-pointer">
                <item.icon className="w-4 h-4 mt-0.5 shrink-0 text-[#a78bfa]" />
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold">{item.label}</div>
                  <div className="text-[11px] text-white/50">{item.note}</div>
                </div>
              </Link>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-white/10 my-1" />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-white/45 font-bold">For artists</DropdownMenuLabel>
          <DropdownMenuItem asChild>
            <Link to="/upload" className="flex items-start gap-3 py-2.5 cursor-pointer">
              <Upload className="w-4 h-4 mt-0.5 shrink-0 text-[#22d3ee]" />
              <div><div className="text-[13.5px] font-semibold">Upload a track or video</div><div className="text-[11px] text-white/50">VidCreForge hosting · free</div></div>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/reapercast/signup" className="flex items-start gap-3 py-2.5 cursor-pointer">
              <Crown className="w-4 h-4 mt-0.5 shrink-0 text-[#ffcf52]" />
              <div><div className="text-[13.5px] font-semibold">Artist signup</div><div className="text-[11px] text-white/50">Release music on ReaperCast</div></div>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
}
